import { parseJwt } from './helpers';
import { getTokenUser, destroyToken, clearStorage } from '../services/auth';

/**
 * Get raw token string from storage
 * @returns {string|null} Raw JWT token string
 */
const getRawToken = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  const rawToken = window.localStorage.getItem("SN-BACKOFFICE-TOKEN");

  if (!rawToken) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawToken);

    if (typeof parsed === 'string' && parsed.length > 0) {
      return parsed;
    }

    if (typeof parsed === 'object' && parsed !== null) {
      if (parsed.token) return parsed.token;
      if (parsed.access_token) return parsed.access_token;
      if (parsed.jwt) return parsed.jwt;
    }

    return null;
  } catch (e) {
    if (rawToken.split('.').length === 3) {
      return rawToken;
    }

    console.warn('Invalid token format in storage:', e);
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param {string} token - JWT token string (optional, will get from storage if not provided)
 * @returns {boolean} true if expired, false if still valid
 */
export const isTokenExpired = (token) => {
  // If no token provided, get from storage
  if (!token) {
    token = getRawToken();
  }

  if (!token || typeof token !== 'string') return true;

  try {
    const decoded = parseJwt(token);

    if (!decoded || !decoded.exp) {
      console.warn('Token does not have expiration field');
      return true;
    }

    const currentTime = Date.now() / 1000;
    const isExpired = decoded.exp < currentTime;

    if (isExpired) {
      if (process.env.NODE_ENV === 'development') { 
        console.warn('Token has expired at:', new Date(decoded.exp * 1000));
      }
    }

    return isExpired;
  } catch (error) {
    console.error('Error parsing token:', error);
    return true;
  }
};

/**
 * Get remaining time until token expires (in seconds)
 * @param {string} token - JWT token string (optional, will get from storage if not provided)
 * @returns {number} seconds until expiration, or 0 if expired/invalid
 */
export const getTokenRemainingTime = (token) => {
  if (!token) {
    token = getRawToken();
  }

  if (!token || typeof token !== 'string') return 0;

  try {
    const decoded = parseJwt(token);

    if (!decoded || !decoded.exp) {
      return 0;
    }

    const currentTime = Date.now() / 1000;
    const remainingTime = decoded.exp - currentTime;

    return remainingTime > 0 ? Math.floor(remainingTime) : 0;
  } catch (error) {
    console.error('Error calculating remaining time:', error);
    return 0;
  }
};

/**
 * Check if token will expire soon (within specified minutes)
 * @param {string} token - JWT token string (optional, will get from storage if not provided)
 * @param {number} minutesThreshold - Minutes before expiration to trigger warning (default: 5)
 * @returns {boolean} true if token will expire soon
 */
export const isTokenExpiringSoon = (token, minutesThreshold = 5) => {
  // If no token provided, get from storage
  if (!token) {
    token = getRawToken();
  }

  const remainingSeconds = getTokenRemainingTime(token);
  const thresholdSeconds = minutesThreshold * 60;

  return remainingSeconds > 0 && remainingSeconds <= thresholdSeconds;
};

/**
 * Validate current session token
 * @returns {object} { isValid: boolean, isExpired: boolean, remainingTime: number }
 */
export const validateSession = () => {
  const token = getRawToken();

  if (!token) {
    return {
      isValid: false,
      isExpired: true,
      remainingTime: 0,
      message: 'No token found'
    };
  }

  const expired = isTokenExpired(token);
  const remainingTime = getTokenRemainingTime(token);

  return {
    isValid: !expired,
    isExpired: expired,
    remainingTime,
    message: expired ? 'Token has expired' : 'Token is valid'
  };
};

/**
 * Handle session expiration - clear storage and redirect
 * @param {function} onExpire - Optional callback before redirect
 */
export const handleSessionExpiration = (onExpire) => {
  console.warn('Session expired - cleaning up and redirecting to login');

  // Execute callback if provided (e.g., show notification)
  if (typeof onExpire === 'function') {
    onExpire();
  }

  // Clear all auth data
  destroyToken();
  clearStorage();

  // Small delay to allow notification to show
  setTimeout(() => {
    window.location.href = '/';
  }, 1000);
};

/**
 * Format remaining time to human-readable string
 * @param {number} seconds - Remaining seconds
 * @returns {string} Formatted time string
 */
export const formatRemainingTime = (seconds) => {
  if (seconds <= 0) return 'Expired';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};
